import React, {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  forwardRef,
  useImperativeHandle,
} from 'react';
import {ISelectItem} from '../../../shared/types';
import ArrDataGrid from '../../common/arr-data-grid/ArrDataGrid';
import {
  GridSortModel,
  GridRenderCellParams,
  useGridApiRef,
} from '@mui/x-data-grid';
import {Box, Typography} from '@mui/material';
import {keywordListColumns} from './KeywordHighlightListColumns';
import {t} from 'i18next';
import {
  useDeleteKeywordHighlight,
  useGetKeywordHighlightList,
} from '../../../hooks/document/useDocument';
import {PAGE_SIZE, ToastType} from '../../../shared/constants';
import {ReactComponent as NoDataIcon} from '../../../assets/images/no-data-found.svg';
import {isEqual, sortBy} from 'lodash';
import {IKeywordHighlightListDataPayload} from '../../../api/types/document.type';
import ArrModal from '../../common/arr-modal/ArrModal';
import ArrToastNotification from '../../common/arr-toast-notification/ArrToastNotification';
import {
  AddEditKeywordHighlightModal,
  AddEditKeywordHightlightdModalRef,
} from '../../KeywordHighlighting/add-edit-keyword-highlight/AddEditKeywordModal';

interface IKeywordHighlightListGridProps {
  selectedGroup: ISelectItem | null;
}

export interface KeywordHighlightListGridRef {
  refreshList: () => void;
}

const KeywordHighlightListGrid = forwardRef<
  KeywordHighlightListGridRef,
  IKeywordHighlightListGridProps
>(({selectedGroup}, ref) => {
  const isOnlyPageChangeRef = useRef<boolean>(false);
  const [pageSize] = useState(PAGE_SIZE);
  const [pageNumber, setPageNumber] = useState(1);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    {field: 'groupName', sort: 'asc'},
  ]);
  const [currentItem, setCurrentItem] = useState<GridRenderCellParams | null>(
    null
  );
  const apiRef = useGridApiRef();
  const addEditKeywordGrpRef = useRef<AddEditKeywordHightlightdModalRef>(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState(ToastType.Success);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const refreshList = () => {
    refetch();
  };

  useImperativeHandle(ref, () => ({
    refreshList,
  }));

  const previousSelectedGroupRef = useRef<{
    selectedGroup: ISelectItem | null;
    sortModel: GridSortModel;
  }>({
    selectedGroup: null,
    sortModel: [{field: 'groupName', sort: 'asc'}],
  });

  const isResetPageNumber = useCallback(() => {
    let resetPageNumber = false;

    if (
      previousSelectedGroupRef.current?.selectedGroup?.id !== selectedGroup?.id
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        selectedGroup,
      };
      resetPageNumber = true;
    } else if (
      !isEqual(
        sortBy(previousSelectedGroupRef.current?.sortModel, ['field', 'sort']),
        sortBy(sortModel, ['field', 'sort'])
      )
    ) {
      previousSelectedGroupRef.current = {
        ...previousSelectedGroupRef.current,
        sortModel,
      };
      resetPageNumber = true;
    }

    return resetPageNumber;
  }, [selectedGroup, sortModel]);

  const queryParams: IKeywordHighlightListDataPayload = useMemo(() => {
    const getPageNumber = isOnlyPageChangeRef.current ? pageNumber : 1;

    const params: IKeywordHighlightListDataPayload = {
      PageSize: pageSize,
      PageNumber: getPageNumber,
      SortingOrder: sortModel[0]?.sort || 'asc',
      SortingColumn: sortModel[0]?.field || 'groupName',
    };

    if (selectedGroup?.id) {
      params.SecurityGroupId = selectedGroup.id;
    }

    return params;
  }, [pageSize, pageNumber, sortModel, selectedGroup]);

  const {
    data: keywordHighlightList,
    isLoading,
    refetch,
  } = useGetKeywordHighlightList(queryParams);

  const {mutate: deleteKeywordHighlight} = useDeleteKeywordHighlight();

  useEffect(() => {
    if (isResetPageNumber() && !isOnlyPageChangeRef.current) {
      setTimeout(() => setPageNumber(1), 100);
    }
    if (isOnlyPageChangeRef.current) {
      isOnlyPageChangeRef.current = false;
    }
    refetch();
  }, [pageNumber, sortModel, selectedGroup]);

  const rows = useMemo(() => {
    return (
      keywordHighlightList?.keywordData?.map((item: any, index: number) => ({
        id: item.keywordID ?? index,
        groupName: item.keywordName,
        colorTypeRGB: item.colorTypeRGB,
        colorCode: item.colorCode,
        description: item.description,
        keywords: item.keywords,
      })) || []
    );
  }, [keywordHighlightList]);

  const NoRowsOverlay = useCallback(
    () => (
      <Box className="no-project-data-found">
        <NoDataIcon />
        <Typography
          tabindex={0}
          className="typo-text headline3_size no-list-text"
          data-testid={`keyword-highlight-list-grid-message-no-data`}>
          {t('keywordHighlighting.messageNoData')}
        </Typography>
      </Box>
    ),
    []
  );

  const handleSortModelChange = (model: GridSortModel) => {
    isOnlyPageChangeRef.current = false;
    setSortModel(model.length ? model : [{field: 'groupName', sort: 'asc'}]);
  };

  const handlePageChange = (value: number) => {
    isOnlyPageChangeRef.current = true;
    setPageNumber(value);
  };

  const confirmDelete = () => {
    deleteKeywordHighlight(currentItem?.id, {
      onSuccess: () => {
        setShowDeleteModal(false);
        setShowToast(true);
        setToastType(ToastType.Success);
        refetch();
        setToastMessage(t('keywordHighlighting.deleteAction.successMessage'));
        if (!apiRef.current || currentItem?.id == null) return;
        apiRef?.current.setCellFocus(currentItem?.id, 'actions');
      },
      onError: () => {
        setShowDeleteModal(false);
        setShowToast(true);
        setToastType(ToastType.Error);

        setToastMessage(t('keywordHighlighting.deleteAction.errorMessage'));
      },
    });
  };

  const handleDelete = (params: GridRenderCellParams) => {
    setCurrentItem(params);
    setShowDeleteModal(true);
  };

  const handleEdit = (params: GridRenderCellParams) =>  {
    setCurrentItem(params);
    if (addEditKeywordGrpRef.current) {
      addEditKeywordGrpRef.current.open(params.id);
    }
  };

  const reloadKeywordHighlightData = useCallback(() => {
    refetch();
    setTimeout(() => {
      if (!apiRef.current || currentItem?.id == null) return;

      apiRef.current.setCellFocus(currentItem?.id, 'actions');
    }, 0);
    setCurrentItem(null);
  }, [refetch, currentItem, apiRef]);

  return (
    <>
      <ArrDataGrid
        data-testid="keywordHighlight-data-grid"
        rows={rows}
        apiRef={apiRef}
        hideFooter={rows.length === 0}
        className={`keywordHighlightList-grid ${rows.length === 0 ? 'fullHeight' : ''}`}
        columns={keywordListColumns(handleDelete, handleEdit)}
        currentPage={pageNumber}
        setCurrentPage={setPageNumber}
        itemsPerPage={PAGE_SIZE}
        onPageChange={handlePageChange}
        onSortModelChange={handleSortModelChange}
        rowCount={keywordHighlightList?.totalRecords || 0}
        pageSizeOptions={[PAGE_SIZE]}
        loading={isLoading}
        noRowsOverlay={NoRowsOverlay}
        pagination
        primaryKey="id"
        columnHeaderHeight={64}
      />
      <ArrModal
        showModal={showDeleteModal}
        ariaLabel={t('keywordHighlighting.deleteAction.confirm')}
        title={t('keywordHighlighting.deleteAction.confirm')}
        ariaDescription={t('keywordHighlighting.deleteAction.confirmMessage')}
        description={t('keywordHighlighting.deleteAction.confirmMessage')}
        onClose={() => setShowDeleteModal(false)}
        autoFocusCloseButton={true}
        dataTestId="keyword-highlightlist-modal"
        buttons={[
          {
            id: 'cancel-button',
            label: t('common.cancel'),
            dataTestId: 'khl-cancel-button',
            onClick: () => setShowDeleteModal(false),
            variant: 'secondary',
          },
          {
            id: 'status-button',
            label: t('common.delete'),
            dataTestId: 'keyword-delete-confirm-button',
            onClick: () => confirmDelete(),
            variant: 'primary',
          },
        ]}
      />
      {showToast && (
        <ArrToastNotification
          message={toastMessage}
          onClose={() => setShowToast(false)}
          status={[toastType]}
        />
      )}
      <AddEditKeywordHighlightModal
        ref={addEditKeywordGrpRef}
        onSaveSuccess={() => reloadKeywordHighlightData()}
        setShowModal={setShowEditModal}     
        showModal={showEditModal}
      />
    </>
  );
});

export default KeywordHighlightListGrid;
